﻿import { useEffect, useRef } from 'react';

import type { CategoryDataItem, DeliverableDataItem, ProductDataItem } from 'pages/access_codes/components/AssignContent/AssignContent';
import { Alert, Col, Collapse, Form, ListGroup, Modal, Row } from 'react-bootstrap';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { FindSKUContent } from 'pages/sku_management/components/FindSKUContent';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { ContentDeliverable, DeliverableType } from 'apis/TransactionApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { useSKUDetails } from 'pages/sku_management/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { useParams } from 'react-router-dom';
import { useSuperState } from 'hooks';
import { SKUApi } from 'apis/SKUApi';

interface SKUTestEditForm {
    enableQuestionReview: boolean;
    id: number;
    isProctored: boolean;
    maxAttempts: number;
    name: string;
    testMode: boolean;
    trainingMode: boolean;
}

interface SKUDefaultSettingsForm {
    booksPerStudent: number;
    coursesPerCode: number;
    enableQuestionReview: boolean;
    enableStudyGuide: boolean;
    testMode: boolean;
    testsPerStudent: number;
    trainingMode: boolean;
}

const MODE_ITEMS = [
    { id: 1, name: 'Training' },
    { id: 2, name: 'Testing' },
    { id: 3, name: 'Both' }
];

const resolveModeItem = (trainingMode: boolean, testMode: boolean) => {

    if (trainingMode && testMode) return MODE_ITEMS[2];
    if (trainingMode) return MODE_ITEMS[0];
    return MODE_ITEMS[1];

};

const treeViewDataManipulation = (content: void | ContentDeliverable[]): Promise<CategoryDataItem[]> =>

    // eslint-disable-next-line implicit-arrow-linebreak
    new Promise(resolve => {

        if (content && content.length > 0) {

            const treeviewData: any[] = [];
            const initiallyExpanded = content.length <= 500;
            for (let i = 0; i < content.length; i++) {

                const deliverableItem: ContentDeliverable = content[i];
                const categoryIndex = treeviewData.findIndex(
                    (item: any) => item.categoryId === deliverableItem.CategoryId || item.text === deliverableItem.CategoryName
                );
                const productIndex = treeviewData[categoryIndex]?.items.findIndex(
                    (item: any) => item.productId === deliverableItem.ProductId
                );
                if (categoryIndex === -1) {

                    treeviewData.push({
                        categoryId: deliverableItem.CategoryId,
                        deliverableTypeId: deliverableItem.DeliverableTypeId,
                        expanded: initiallyExpanded,
                        items: [
                            {
                                deliverableTypeId: deliverableItem.DeliverableTypeId,
                                expanded: initiallyExpanded,
                                items: [
                                    {
                                        ...deliverableItem,
                                        expanded: initiallyExpanded,
                                        key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                                        text: deliverableItem.Name
                                    }
                                ],
                                key: `${deliverableItem.ProductId}-${deliverableItem.DeliverableTypeId}`,
                                productId: deliverableItem.ProductId,
                                text: deliverableItem.ProductName
                            }
                        ],
                        key: `${deliverableItem.CategoryId}-${deliverableItem.DeliverableTypeId}`,
                        text: deliverableItem.CategoryName
                    });

                }

                if (productIndex === -1) {

                    treeviewData[categoryIndex].items.push({
                        deliverableTypeId: deliverableItem.DeliverableTypeId,
                        expanded: initiallyExpanded,
                        items: [
                            {
                                ...deliverableItem,
                                expanded: initiallyExpanded,
                                key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                                text: deliverableItem.Name
                            }
                        ],
                        key: `${deliverableItem.ProductId}-${deliverableItem.DeliverableTypeId}`,
                        productId: deliverableItem.ProductId,
                        text: deliverableItem.ProductName
                    });

                } else if (treeviewData.length > 0) {

                    treeviewData[categoryIndex]?.items[productIndex].items.push({
                        ...deliverableItem,
                        expanded: initiallyExpanded,
                        key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                        text: deliverableItem.Name
                    });

                }

            }
            resolve(treeviewData);

        } else {

            resolve([]);

        }

    });

interface SKUTreeNodeProps {
    node: any;
    onDelete: (deliverable: ContentDeliverable) => void;
    skuId: number;
    totalListItemCount: number;
}

const SKUTreeNode = ({ node, onDelete, skuId, totalListItemCount }: SKUTreeNodeProps) => {

    const { clearAllToasts, createToast } = useNotification();
    const { pageContext, update } = useSKUDetails();
    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        currentContentIndex: 0,
        editSettingsObject: {
            id: 0,
            name: '',
            enableQuestionReview: false,
            isProctored: false,
            maxAttempts: 0,
            testMode: true,
            trainingMode: true
        } as SKUTestEditForm,
        error: null as string | null,
        loading: false,
        open: totalListItemCount <= 500,
        showEditModal: false
    });
    const { currentContentIndex, editSettingsObject, error, loading, open, showEditModal } = state;

    const hasChildren = !!node?.items?.length;
    const didMountRef = useRef(false);

    useEffect(() => {

        if (!didMountRef.current) {

            didMountRef.current = true;
            return;

        }
        if (showEditModal) return;
        clearAllToasts();

    }, [showEditModal]); // eslint-disable-line react-hooks/exhaustive-deps

    const clearError = () => setState({ error: null });

    const setEditSettingsObject = (next: SKUTestEditForm) => setState({ editSettingsObject: next });

    const handleLoadIcon = (deliverable: ContentDeliverable) => {

        let className = '';
        let deliverableTypeText = '';
        switch (true) {

            case deliverable.DeliverableTypeId === DeliverableType.Test:

                className = 'fa-light fa-ballot-check text-primary';
                deliverableTypeText = formatMessage({ id: 'test.051b8cd1', defaultMessage: 'Test' });
                break;
            case deliverable.DeliverableTypeId === DeliverableType.Book:
                className = 'fa-light fa-book text-success';
                deliverableTypeText = formatMessage({ id: 'ebook.5d34c24a', defaultMessage: 'EBook' });
                break;
            case deliverable.DeliverableTypeId === DeliverableType.Course:
                className = 'fa-light fa-rectangle-history purple';
                deliverableTypeText = formatMessage({ id: 'course.790646f3', defaultMessage: 'Course' });
                break;
            default:
                break;

        }
        return <i className={`me-2 kendo-tree-view__list-item__icon ${className}`} title={deliverableTypeText}></i>;

    };

    const handleEditSettings = (deliverable: ContentDeliverable) => {

        // Find this test's position in the cached content list so we can splice the
        // updated copy back in place after save (mirrors DeliverableContentTreeView).
        if (pageContext?.Data.content) {

            for (let index = 0; index < pageContext.Data.content.length; index++) {

                if (pageContext.Data.content[index].Id === deliverable.Id) {

                    setState({ currentContentIndex: index });
                    break;

                }

            }

        }

        setEditSettingsObject({
            id: deliverable.Id,
            name: deliverable.Name,
            enableQuestionReview: deliverable.AllowReview ?? false,
            isProctored: deliverable.IsProctored ?? false,
            maxAttempts: deliverable.MaxAttempts ?? 0,
            testMode: deliverable.TestingMode ?? false,
            trainingMode: deliverable.TrainingMode ?? false
        });

        setState({ showEditModal: true });

    };

    const handleSaveContentSettings = async () => {

        try {

            if (editSettingsObject.maxAttempts === undefined || Number.isNaN(editSettingsObject.maxAttempts)) {

                createToast(formatMessage({ id: 'max_attempts_cannot_be_left_blank.0779b9aa', defaultMessage: 'Max Attempts cannot be left blank.' }), 'danger');
                return;

            }

            if (!editSettingsObject.testMode && !editSettingsObject.trainingMode) {

                createToast(formatMessage({ id: 'at_least_one_mode_must_be_selected.edfdd556', defaultMessage: 'At least one mode must be selected.' }), 'danger');
                return;

            }

            setState({ loading: true });

            const skuName = pageContext?.Data?.skuDetails?.Name ?? '';
            const result = await SKUApi.UpdateSingleContentSettings(skuName, pageContext?.Data?.skuDetails?.Id ?? 0, [{
                AllowReview: editSettingsObject.enableQuestionReview,
                DeliverableTypeId: DeliverableType.Test,
                Id: editSettingsObject.id,
                IsProctored: editSettingsObject.isProctored,
                MaxAttempts: editSettingsObject.maxAttempts,
                TestingMode: editSettingsObject.testMode,
                TrainingMode: editSettingsObject.trainingMode
            } as ContentDeliverable]);

            if (!result?.Done) {

                throw new Error(result?.Message ?? 'A server error occurred');

            }

            if (pageContext?.Data.content) {

                const copyOfContent = [...pageContext.Data.content];
                copyOfContent[currentContentIndex] = {
                    ...copyOfContent[currentContentIndex],
                    AllowReview: editSettingsObject.enableQuestionReview,
                    IsProctored: editSettingsObject.isProctored,
                    MaxAttempts: editSettingsObject.maxAttempts,
                    TestingMode: editSettingsObject.testMode,
                    TrainingMode: editSettingsObject.trainingMode
                };
                update({ content: copyOfContent });

            }

            createToast(formatMessage({ id: 'name_was_edited_successfully.2b8d4098', defaultMessage: '{name} was edited successfully' }, { name: editSettingsObject.name }), 'success');
            setState({ loading: false, showEditModal: false });

        } catch (e: any) {

            setState({
                error: e?.message || String(e),
                loading: false
            });

        }

    };

    return (
        <>
            <ListGroup.Item className="border-0">
                <div className="d-flex justify-content-between align-items-start">
                    <div
                        onClick={() => hasChildren && setState({ open: !open })}
                        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                    >
                        {hasChildren ? (
                            <>
                                {open ? '▼' : '▶'}
                                {' '}
                                {node.text}
                            </>
                        ) : (
                            <>
                                {handleLoadIcon(node as ContentDeliverable)}
                                {node.text}
                            </>
                        )}
                    </div>

                    {!hasChildren && (
                        <div>
                            {node.DeliverableTypeId === DeliverableType.Test && (
                                <GMetrixButton
                                    className="me-2"
                                    iconClass="fa-regular fa-pencil"
                                    infoTitle={formatMessage({ id: 'edit.00978daf', defaultMessage: 'Edit' })}
                                    onClick={() => handleEditSettings(node as ContentDeliverable)}
                                    variant="outline-primary"
                                >
                                </GMetrixButton>
                            )}

                            <GMetrixButton
                                iconClass="fa-regular fa-trash"
                                infoTitle={formatMessage({ id: 'remove.d34ac1e7', defaultMessage: 'Remove' })}
                                onClick={() => onDelete(node as ContentDeliverable)}
                                variant="outline-danger"
                            >
                            </GMetrixButton>
                        </div>
                    )}
                </div>

                {hasChildren && (
                    <Collapse in={open}>
                        <div className="ms-3 mt-2">
                            <ListGroup variant="flush">
                                {node.items.map((child: any, index: number) => (
                                    <SKUTreeNode
                                        key={index}
                                        node={child}
                                        onDelete={onDelete}
                                        skuId={skuId}
                                        totalListItemCount={totalListItemCount}
                                    />
                                ))}
                            </ListGroup>
                        </div>
                    </Collapse>
                )}
            </ListGroup.Item>

            <GMetrixDialog
                actionButtonClick={handleSaveContentSettings}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                dialogLabel={formatMessage({ id: 'edit_settings.73cda8dc', defaultMessage: 'Edit Settings' })}
                disabled={loading}
                onCancel={() => setState({ showEditModal: false })}
                show={showEditModal}
                size="lg"
            >
                <Alert dismissible onClose={clearError} show={!!error} variant="danger">
                    {error}
                </Alert>
                <Form>
                    <h5 className="mb-3">
                        {editSettingsObject.name}
                    </h5>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsed = parseInt(e.target.value, 10);
                                    setEditSettingsObject({
                                        ...editSettingsObject,
                                        maxAttempts: Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
                                    });

                                }}
                                disabled={loading}
                                label={formatMessage({ id: 'max_attempts.3822990c', defaultMessage: 'Max Attempts' })}
                                type="number"
                                value={editSettingsObject.maxAttempts}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                onChange={mode => {

                                    const selected = Array.isArray(mode) ? mode[0] : mode;
                                    if (!selected) return;
                                    switch (selected.name) {

                                        case 'Training':
                                            setEditSettingsObject({ ...editSettingsObject, testMode: false, trainingMode: true });
                                            break;
                                        case 'Testing':
                                            setEditSettingsObject({ ...editSettingsObject, testMode: true, trainingMode: false });
                                            break;
                                        case 'Both':
                                        default:
                                            setEditSettingsObject({ ...editSettingsObject, testMode: true, trainingMode: true });
                                            break;

                                    }

                                }}
                                dataItemKey="id"
                                dataItems={MODE_ITEMS}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'training_unlimited_time_with_detailed_fe.cbfaabb1', defaultMessage: 'Training - Unlimited time with detailed feedback Testing - Timed with no detailed feedback' })}
                                label={formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}
                                textField="name"
                                value={resolveModeItem(editSettingsObject.trainingMode, editSettingsObject.testMode)}
                            />
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixCheckInput
                                checked={editSettingsObject.isProctored}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'when_enabled_a_proctor_must_sign_in_befo.fe1f9085', defaultMessage: 'When enabled, a proctor must sign-in before the test begins' })}
                                label={formatMessage({ id: 'proctoring.4139a0f9', defaultMessage: 'Proctoring' })}
                                onChange={e => setEditSettingsObject({ ...editSettingsObject, isProctored: e.target.checked })}
                                type="switch"
                            />
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixCheckInput
                                checked={editSettingsObject.enableQuestionReview}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'adds_question_review.5e6fb3bf', defaultMessage: 'Adds question review' })}
                                label={formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })}
                                onChange={e => setEditSettingsObject({ ...editSettingsObject, enableQuestionReview: e.target.checked })}
                                type="checkbox"
                            />
                        </Col>
                    </Row>
                </Form>
            </GMetrixDialog>
        </>
    );

};

export const SKUContent = () => {

    const [state, setState] = useSuperState({
        defaultSettingsForm: {
            booksPerStudent: 0,
            coursesPerCode: 0,
            enableQuestionReview: false,
            enableStudyGuide: false,
            testMode: false,
            testsPerStudent: 0,
            trainingMode: false
        } as SKUDefaultSettingsForm,
        error: null as string | null,
        loading: false,
        search: '',
        showAddContentModal: false,
        showDefaultEditModal: false,
        treeViewData: [] as CategoryDataItem[]
    });
    const { defaultSettingsForm, error, loading, search, showAddContentModal, showDefaultEditModal, treeViewData } = state;
    const { formatMessage } = useIntl();
    const { pageContext, update } = useSKUDetails();
    const skuData = pageContext?.Data;
    const content = skuData?.content;
    const skuSettings = skuData?.skuDetails?.Settings;

    const { id } = useParams();
    const skuId = Number(id);

    const { createToast } = useNotification();

    const clearError = () => setState({ error: null });

    const setDefaultSettingsForm = (next: SKUDefaultSettingsForm) => setState({ defaultSettingsForm: next });

    const updateDefaultSettingsForm = (updates: Partial<SKUDefaultSettingsForm>) => setDefaultSettingsForm({ ...state.defaultSettingsForm, ...updates });

    const parseNumberValue = (value: string, fallback = 0, max = Number.MAX_SAFE_INTEGER) => {

        const parsedNumber = parseInt(value, 10);
        if (Number.isNaN(parsedNumber)) {

            return fallback;

        }

        if (parsedNumber < 0) {

            return 0;

        }

        if (parsedNumber > max) {

            return max;

        }

        return parsedNumber;

    };

    const handleSaveDefaultSettings = async () => {

        try {

            const skuName = pageContext?.Data?.skuDetails?.Name ?? '';
            if (!skuName) {

                createToast(formatMessage({ id: 'sku_name_is_missing.f535cfdf', defaultMessage: 'SKU name is missing.' }), 'danger');
                return;

            }

            if (Number.isNaN(defaultSettingsForm.testsPerStudent)
                || Number.isNaN(defaultSettingsForm.coursesPerCode)
                || Number.isNaN(defaultSettingsForm.booksPerStudent)) {

                createToast(formatMessage({ id: 'numeric_fields_cannot_be_left_blank.16609766', defaultMessage: 'Numeric fields cannot be left blank.' }), 'danger');
                return;

            }

            setState({ loading: true });

            const existing = skuSettings ?? ({} as NonNullable<typeof skuSettings>);
            const payload = {
                AllowReview: defaultSettingsForm.enableQuestionReview,
                AllowStudyGuide: defaultSettingsForm.enableStudyGuide,
                AssociateProduct: existing.AssociateProduct ?? false,
                AttemptsPerTest: existing.AttemptsPerTest ?? 0,
                BooksAllowed: defaultSettingsForm.booksPerStudent,
                CoursesAllowed: defaultSettingsForm.coursesPerCode,
                DefaultPartnerId: existing.DefaultPartnerId ?? 0,
                NumberOfRedemptions: existing.NumberOfRedemptions ?? 0,
                TestingMode: defaultSettingsForm.testMode,
                TestsAllowed: defaultSettingsForm.testsPerStudent,
                TrainingMode: defaultSettingsForm.trainingMode
            };

            const result = await SKUApi.UpdateDefaultContentSettings(skuName, skuId, payload);

            if (!result?.Done) {

                throw new Error(result?.Message ?? 'A server error occurred');

            }

            // Re-fetch content so per-test overlays reflect any propagated granular changes.
            const refreshed = await SKUApi.GetContentBySKU(skuName);
            update({
                content: refreshed ?? [],
                skuDetails: pageContext?.Data?.skuDetails
                    ? { ...pageContext.Data.skuDetails, Settings: { ...pageContext.Data.skuDetails.Settings, ...payload } }
                    : pageContext?.Data?.skuDetails
            });

            createToast(formatMessage({ id: 'default_settings_updated_successfully.e2058391', defaultMessage: 'Default settings updated successfully' }), 'success');
            setState({ loading: false, showDefaultEditModal: false });

        } catch (e: any) {

            setState({
                error: e?.message || String(e),
                loading: false
            });

        }

    };

    const handleDeleteContent = async (deliverable: ContentDeliverable) => {

        if (!content) return;

        if (content.length <= 1) {

            createToast(formatMessage({ id: 'a_sku_must_contain_at_least_one_test_cou.e5a1311f', defaultMessage: 'A SKU must contain at least one test, course, or book.' }), 'danger');
            return;

        }

        try {

            const skuName = pageContext?.Data?.skuDetails?.Name ?? '';
            if (!skuName) {

                createToast(formatMessage({ id: 'sku_name_is_missing.f535cfdf', defaultMessage: 'SKU name is missing.' }), 'danger');
                return;

            }

            setState({ loading: true });

            const nextContent = content.filter(
                c => !(c.Id === deliverable.Id && c.DeliverableTypeId === deliverable.DeliverableTypeId)
            );

            const sanitized = nextContent.filter(c => !c.IsCustomTest);

            const result = await SKUApi.CreateAlterSKU({
                Content: sanitized,
                SKU: skuName,
                SkuId: skuId
            });

            if (!result?.Done) {

                throw new Error(result?.Message ?? 'A server error occurred');

            }

            const refreshed = await SKUApi.GetContentBySKU(skuName);
            update({ content: refreshed ?? [] });

            createToast(formatMessage({ id: 'the_content_was_saved_successfully.6c899203', defaultMessage: 'The content was saved successfully' }), 'success');
            setState({ loading: false });

        } catch (e: any) {

            setState({
                error: e?.message || String(e),
                loading: false
            });
            createToast(e?.message || formatMessage({ id: 'failed_to_remove_content.901ba89c', defaultMessage: 'Failed to remove content.' }), 'danger');

        }

    };

    const handleSaveAddedContent = async ({ content: nextContent }: { content: ContentDeliverable[] }) => {

        try {

            const skuName = pageContext?.Data?.skuDetails?.Name ?? '';
            if (!skuName) {

                createToast(formatMessage({ id: 'sku_name_is_missing.f535cfdf', defaultMessage: 'SKU name is missing.' }), 'danger');
                return;

            }

            setState({ loading: true });

            // Custom tests are not supported for SKUs; strip defensively.
            const sanitized = nextContent.filter(c => !c.IsCustomTest);

            const result = await SKUApi.CreateAlterSKU({
                Content: sanitized,
                SKU: skuName,
                SkuId: skuId
            });

            if (!result?.Done) {

                throw new Error(result?.Message ?? 'A server error occurred');

            }

            // Re-fetch so per-test granular overlays / mode pairing reflect any
            // additions or removals that touched SettingTypeID = 8.
            const refreshed = await SKUApi.GetContentBySKU(skuName);
            update({ content: refreshed ?? [] });

            createToast(formatMessage({ id: 'content_updated_successfully.a4bcc67a', defaultMessage: 'Content updated successfully' }), 'success');
            setState({ loading: false, showAddContentModal: false });

        } catch (e: any) {

            setState({
                error: e?.message || String(e),
                loading: false
            });
            createToast(e?.message || formatMessage({ id: 'failed_to_update_sku_content.c05e5692', defaultMessage: 'Failed to update SKU content.' }), 'danger');

        }

    };

    const hierarchyFilter = (data: CategoryDataItem[]): CategoryDataItem[] => {

        const hierarchySearchedData: CategoryDataItem[] = [];

        data.map((categoryItem: CategoryDataItem) => categoryItem.items.map((productItem: ProductDataItem) => productItem.items.map((dataItem: DeliverableDataItem) => {

            if (dataItem.text && dataItem.text.toLowerCase().includes(search.toLowerCase())) {

                let categoryIndex = hierarchySearchedData.findIndex(
                    (category: CategoryDataItem) => category.categoryId === categoryItem.categoryId
                );
                let productIndex = -1;
                if (categoryIndex === -1) {

                    hierarchySearchedData.push({ ...categoryItem, items: [] });
                    categoryIndex = hierarchySearchedData.findIndex(
                        (category: CategoryDataItem) => category.categoryId === categoryItem.categoryId
                    );

                } else {

                    productIndex = hierarchySearchedData[categoryIndex].items.findIndex(
                        (product: ProductDataItem) => product.productId === productItem.productId
                    );

                }
                if (productIndex === -1) {

                    hierarchySearchedData[categoryIndex].items.push({ ...productItem, items: [] });
                    productIndex = hierarchySearchedData[categoryIndex].items.findIndex(
                        (product: ProductDataItem) => product.productId === productItem.productId
                    );

                }
                hierarchySearchedData[categoryIndex].items[productIndex].items.push(dataItem);

            }

        })));

        return hierarchySearchedData;

    };

    // Hydrate default settings form from SKU settings whenever they change or the modal opens.
    useEffect(() => {

        if (!skuSettings) return;

        const settings = skuSettings as typeof skuSettings & { TestingMode?: boolean; TrainingMode?: boolean };

        setDefaultSettingsForm({
            booksPerStudent: settings.BooksAllowed ?? 0,
            coursesPerCode: settings.CoursesAllowed ?? 0,
            enableQuestionReview: settings.AllowReview ?? false,
            enableStudyGuide: settings.AllowStudyGuide ?? false,
            testMode: settings.TestingMode ?? false,
            testsPerStudent: settings.TestsAllowed ?? 0,
            trainingMode: settings.TrainingMode ?? false
        });

    }, [skuSettings, showDefaultEditModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch content once per tab when not already cached on SKUData.
    useEffect(() => {

        if (!pageContext) return;
        if (!skuId || Number.isNaN(skuId)) return;
        if (content !== undefined) return;

        let cancelled = false;

        (async () => {

            try {

                setState({ loading: true });
                const skuName = pageContext?.Data?.skuDetails?.Name ?? '';
                if (!skuName) {

                    if (!cancelled) update({ content: [] });
                    return;

                }
                const result = await SKUApi.GetContentBySKU(skuName);
                if (cancelled) return;
                update({ content: result ?? [] });

                console.log('result ', result);

            } catch (e) {

                console.error(e);
                if (!cancelled) update({ content: [] });

            } finally {

                if (!cancelled) setState({ loading: false });

            }

        })();

        return () => {

            cancelled = true;

        };

    }, [skuId, pageContext?.Id, content]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-run the tree transform whenever the cached content changes.
    useEffect(() => {

        if (!content) {

            setState({ treeViewData: [] });
            return;

        }

        (async () => {

            try {

                const nextTreeViewData = await treeViewDataManipulation(content);
                setState({ treeViewData: nextTreeViewData });

            } catch (e) {

                console.error(e);

            }

        })();

    }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <GMetrixDialog
                actionButtonClick={handleSaveDefaultSettings}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                dialogLabel={formatMessage({ id: 'edit_default_settings.cf08e662', defaultMessage: 'Edit Default Settings' })}
                disabled={loading}
                onCancel={() => setState({ loading: false, showDefaultEditModal: false })}
                show={showDefaultEditModal}
                size="xl"
            >

                <Alert dismissible onClose={clearError} show={!!error} variant="danger">
                    {error}
                </Alert>

                <Form>
                    {/* Test Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Test Settings" id="test_settings.dfedf589" />
                    </h5>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsedValue = parseNumberValue(e.target.value, defaultSettingsForm.testsPerStudent);
                                    updateDefaultSettingsForm({ testsPerStudent: parsedValue });

                                }}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_test_attempts_that_a.a29c0375', defaultMessage: 'Limit the number of Test Attempts that a student can take across all Tests in this SKU' })}
                                label={formatMessage({ id: 'tests_per_student.44f8c927', defaultMessage: 'Tests per Student' })}
                                type="number"
                                value={defaultSettingsForm.testsPerStudent}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                onChange={mode => {

                                    const selectedMode = Array.isArray(mode) ? mode[0] : mode;
                                    if (!selectedMode) return;

                                    switch (selectedMode.name) {

                                        case 'Training':
                                            updateDefaultSettingsForm({ testMode: false, trainingMode: true });
                                            break;
                                        case 'Testing':
                                            updateDefaultSettingsForm({ testMode: true, trainingMode: false });
                                            break;
                                        case 'Both':
                                        default:
                                            updateDefaultSettingsForm({ testMode: true, trainingMode: true });
                                            break;

                                    }

                                }}
                                dataItemKey="id"
                                dataItems={MODE_ITEMS}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'training_unlimited_time_with_detailed_fe.cbfaabb1', defaultMessage: 'Training - Unlimited time with detailed feedback Testing - Timed with no detailed feedback' })}
                                label={formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}
                                textField="name"
                                value={resolveModeItem(defaultSettingsForm.trainingMode, defaultSettingsForm.testMode)}
                            />
                        </Col>
                    </Row>

                    <div className="d-flex">
                        <GMetrixCheckInput
                            checked={defaultSettingsForm.enableQuestionReview}
                            disabled={loading}
                            infoTitle={formatMessage({ id: 'permit_students_to_review_missed_questio.bdd3f3df', defaultMessage: 'Permit students to review missed questions without changing the score in Testing Mode.' })}
                            label={formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })}
                            onChange={e => updateDefaultSettingsForm({ enableQuestionReview: e.target.checked })}
                            type="checkbox"
                        />
                        <GMetrixCheckInput
                            checked={defaultSettingsForm.enableStudyGuide}
                            disabled={loading}
                            infoTitle={formatMessage({ id: 'permit_access_to_the_gmetrix_study_guide.ad613c7b', defaultMessage: 'Permit access to the Gmetrix Study Guides when available.' })}
                            label={formatMessage({ id: 'study_guide.8c412365', defaultMessage: 'Study Guide' })}
                            onChange={e => updateDefaultSettingsForm({ enableStudyGuide: e.target.checked })}
                            type="checkbox"
                        />
                    </div>

                    <hr />

                    {/* Course Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Course Settings" id="course_settings.4f8c306c" />
                    </h5>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsedValue = parseNumberValue(e.target.value, defaultSettingsForm.coursesPerCode);
                                    updateDefaultSettingsForm({ coursesPerCode: parsedValue });

                                }}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_courses_that_a_stude.ae44cb39', defaultMessage: 'Limit the number of Courses that a student can take across all Courses in this SKU' })}
                                label={formatMessage({ id: 'courses_per_code.139bd3bd', defaultMessage: 'Courses Per Code' })}
                                type="number"
                                value={defaultSettingsForm.coursesPerCode}
                            />
                        </Col>
                    </Row>

                    <hr />

                    {/* Book Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Book Settings" id="book_settings.ae00f5eb" />
                    </h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsedValue = parseNumberValue(e.target.value, defaultSettingsForm.booksPerStudent);
                                    updateDefaultSettingsForm({ booksPerStudent: parsedValue });

                                }}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_books_that_a_student.02f38157', defaultMessage: 'Limit the number of Books that a student can read across all Books in this SKU' })}
                                label={formatMessage({ id: 'books_per_student.36f8ef23', defaultMessage: 'Books Per Student' })}
                                type="number"
                                value={defaultSettingsForm.booksPerStudent}
                            />
                        </Col>
                    </Row>
                </Form>
            </GMetrixDialog>

            <Row className="mb-3">
                <Col md={4}>
                    <GMetrixInput
                        disabled={loading}
                        onChange={e => setState({ search: e.target.value })}
                        placeholder={formatMessage({ id: 'search_courses_tests_or_assignments.b8c4464f', defaultMessage: 'Search courses, tests, or assignments...' })}
                        type="text"
                        value={search}
                    />
                </Col>
                <Col className="text-end" md={8}>
                    <GMetrixButton
                        className="me-2"
                        disabled={loading}
                        iconClass="fa-regular fa-cog"
                        onClick={() => setState({ showDefaultEditModal: true })}
                        variant="outline-primary"
                    >
                        <FormattedMessage defaultMessage="Default Settings" id="default_settings.e49d564c" />
                    </GMetrixButton>
                    <GMetrixButton
                        disabled={loading}
                        iconClass="fa-regular fa-plus"
                        onClick={() => setState({ showAddContentModal: true })}
                        variant="primary"
                    >
                        <FormattedMessage defaultMessage="Add Content" id="add_content.aad4180d" />
                    </GMetrixButton>
                </Col>
            </Row>

            <Modal
                onHide={() => setState({ showAddContentModal: false })}
                show={showAddContentModal}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="w-100">
                        <FormattedMessage defaultMessage="Add Content" id="add_content.aad4180d" />
                    </Modal.Title>
                </Modal.Header>
                {showAddContentModal && (
                    <FindSKUContent
                        ActionsWrapper={Modal.Footer}
                        contentLoader={() => SKUApi.GetAllContent()}
                        ContentWrapper={Modal.Body}
                        onClose={() => setState({ showAddContentModal: false })}
                        onSuccess={handleSaveAddedContent}
                        saveButtonLabel="Save"
                        selectedContent={content ?? []}
                    />
                )}
            </Modal>

            {loading ? (
                <div className='py-3 text-center'>
                    <GMetrixLoader />
                </div>
            ) : treeViewData.length > 0 && content ? (
                <ListGroup style={{ maxHeight: '60vh', overflowY: 'scroll' }} variant="flush">
                    {hierarchyFilter(treeViewData).map((node, index) => (
                        <SKUTreeNode
                            key={index}
                            node={node}
                            onDelete={handleDeleteContent}
                            skuId={skuId}
                            totalListItemCount={content.length}
                        />
                    ))}
                </ListGroup>
            ) : (
                <div className="py-3 text-center content-settings__no-resources">
                    <FormattedMessage defaultMessage="No content has been added." id="no_content_has_been_added.9e012e26" />
                </div>
            )}
        </>
    );

};
